import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Member } from '@app/app/page-modules/programs/models/member.model';
import { Program, ProgramItem } from '@app/app/page-modules/programs/models/program.model';
import { PlaylistType } from '@app/app/shared/models';
import { Page } from '@app/app/shared/models/page';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { concat } from 'rxjs';
import { catchError, switchMap, toArray } from 'rxjs/operators';
import {
  CollectionMembersRequest,
  CreateProgramFromTemplateRequest,
  CreateProgramResponse,
  ProgramCreationRequest,
  ProgramDataService,
  ProgramSectionBulkUpdateItem,
  ProgramSectionRequestUnion,
  ProgramSectionResponse,
  ProgramUpdateRequest,
  ProgramsRequest,
  UpdateProgramResponse,
} from './data.service';

@Injectable()
export class ApiProgramDataService implements ProgramDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getPrograms(orgClientId?: string, requestParams?: ProgramsRequest): ObservableResult<Page<ProgramItem>> {
    const params: Record<string, string> = {};
    if (orgClientId) {
      params['orgClientId'] = orgClientId;
    }
    if (requestParams?.page != null) {
      params['page'] = requestParams.page.toString();
    }
    if (requestParams?.size != null) {
      params['size'] = requestParams.size.toString();
    }
    if (requestParams?.term) {
      params['term'] = requestParams.term;
    }
    if (requestParams?.sortBy) {
      params['sortBy'] = requestParams.sortBy;
    }
    return this.client.get<Page<ProgramItem>>(Location.joinWithSlash(environment.apiRootUrl || '', 'programs'), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAllPrograms'))),
    );
  }

  getProgramsAnalytics(orgClientId?: string, requestParams?: ProgramsRequest): ObservableResult<Page<ProgramItem>> {
    return this.getAdminList('analytics/programs/admin', orgClientId, requestParams);
  }

  getCollections(orgClientId?: string, requestParams?: ProgramsRequest): ObservableResult<Page<ProgramItem>> {
    const params: Record<string, string> = {};
    if (orgClientId) {
      params['orgClientId'] = orgClientId;
    }
    if (requestParams?.page != null) {
      params['page'] = requestParams.page.toString();
    }
    if (requestParams?.size != null) {
      params['size'] = requestParams.size.toString();
    }
    if (requestParams?.term) {
      params['term'] = requestParams.term;
    }
    if (requestParams?.sortBy) {
      params['sortBy'] = requestParams.sortBy;
    }
    return this.client.get<Page<ProgramItem>>(Location.joinWithSlash(environment.apiRootUrl || '', 'collections'), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAllPrograms'))),
    );
  }

  private getAdminList(path: string, orgClientId?: string, requestParams?: ProgramsRequest): ObservableResult<Page<ProgramItem>> {
    const params: Record<string, string> = {};
    if (orgClientId) {
      params['orgClientId'] = orgClientId;
    }
    if (requestParams?.page != null) {
      params['page'] = requestParams.page.toString();
    }
    if (requestParams?.size != null) {
      params['size'] = requestParams.size.toString();
    }
    if (requestParams?.term) {
      params['term'] = requestParams.term;
    }
    if (requestParams?.sortBy) {
      params['sortBy'] = requestParams.sortBy;
    }
    return this.client.get<Page<ProgramItem>>(Location.joinWithSlash(environment.apiRootUrl || '', path), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAllPrograms'))),
    );
  }

  getProgram(programUri: string, languageCode?: string, playlistPath?: string): ObservableResult<Program> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `programs/${programUri}`);
    const params = playlistPath ? { in: playlistPath } : null;

    return this.client.get<Program>(url, params, languageCode ? { 'Accept-Language': languageCode } : null).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetProgramDetail'))),
    );
  }

  createProgram(request: ProgramCreationRequest): ObservableResult<CreateProgramResponse> {
    return this.createPlaylist(request, 'PROGRAM');
  }

  private createPlaylist(request: ProgramCreationRequest, type: PlaylistType): ObservableResult<CreateProgramResponse> {
    const requestBody = { ...request, type };

    return this.client
      .post<CreateProgramResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', 'playlists'),
        requestBody,
        undefined,
        request.languageCode ? { 'Content-Language': request.languageCode } : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err) => ObservableResult.ofError(err)),
      );
  }

  createProgramFromTemplate(request: CreateProgramFromTemplateRequest): ObservableResult<CreateProgramResponse> {
    const queryParams: { [key: string]: string } = {};
    if (request.in) {
      queryParams['in'] = request.in;
    }
    if (request.section) {
      queryParams['section'] = request.section;
    }

    const url = Location.joinWithSlash(environment.apiRootUrl || '', `playlists/templates/${request.templateUid}`);
    const headers = request.languageCode ? { 'Content-Language': request.languageCode } : null;

    return this.client.post<CreateProgramResponse>(url, {}, Object.keys(queryParams).length > 0 ? queryParams : undefined, headers).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err) => ObservableResult.ofError(err)),
    );
  }

  updateProgram(programUid: string, request: ProgramUpdateRequest, languageCode?: string): ObservableResult<UpdateProgramResponse> {
    return this.client
      .patch<UpdateProgramResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${programUid}`),
        request,
        {},
        languageCode ? { 'Content-Language': languageCode } : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateProgram'))),
      );
  }

  addProgramSection(
    programUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse> {
    return this.addSection('programs', programUid, request, languageCode);
  }

  updateProgramSection(
    programUid: string,
    sectionUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse> {
    return this.updateSection('programs', programUid, sectionUid, request, languageCode);
  }

  bulkUpdateProgramSections(programUid: string, sections: ProgramSectionBulkUpdateItem[]): ObservableResult<void> {
    return this.bulkUpdateSections('programs', programUid, sections);
  }

  deleteProgramSection(programUid: string, sectionUid: string): ObservableResult<void> {
    return this.deleteSection('programs', programUid, sectionUid);
  }

  reorderProgramSections(programUid: string, request: { sections: string[] }): ObservableResult<void> {
    return this.reorderSections('programs', programUid, request);
  }

  private addSection(
    basePath: string,
    playlistUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse> {
    return this.client
      .put<ProgramSectionResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', `${basePath}/${playlistUid}/sections`),
        request,
        {},
        languageCode ? { 'Content-Language': languageCode, 'Accept-Language': languageCode } : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body || {})),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorCreateSection'))),
      );
  }

  private updateSection(
    basePath: string,
    playlistUid: string,
    sectionUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse> {
    return this.client
      .post<ProgramSectionResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', `${basePath}/${playlistUid}/sections/${sectionUid}`),
        request,
        {},
        languageCode ? { 'Content-Language': languageCode, 'Accept-Language': languageCode } : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body || {})),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateSection'))),
      );
  }

  private bulkUpdateSections(basePath: string, playlistUid: string, sections: ProgramSectionBulkUpdateItem[]): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `${basePath}/${playlistUid}/sections`);
    const sectionsByLanguage = new Map<string, ProgramSectionBulkUpdateItem[]>();

    for (const item of sections) {
      const languageCode = item.languageCode || '';
      const group = sectionsByLanguage.get(languageCode);
      if (group) {
        group.push(item);
      } else {
        sectionsByLanguage.set(languageCode, [item]);
      }
    }

    const requests = Array.from(sectionsByLanguage.entries()).map(([languageCode, items]) =>
      this.client.post<void>(
        url,
        items.map((item) => ({ uid: item.uid, ...item.request })),
        {},
        languageCode ? { 'Content-Language': languageCode, 'Accept-Language': languageCode } : null,
      ),
    );

    if (!requests.length) {
      return ObservableResult.ofSuccess();
    }

    return concat(...requests).pipe(
      toArray(),
      switchMap(() => ObservableResult.ofSuccess()),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateSections'))),
    );
  }

  private deleteSection(basePath: string, playlistUid: string, sectionUid: string): ObservableResult<void> {
    return this.client
      .delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', `${basePath}/${playlistUid}/sections/${sectionUid}`))
      .pipe(
        switchMap(() => ObservableResult.ofSuccess()),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRemovingSection'))),
      );
  }

  private reorderSections(basePath: string, playlistUid: string, request: { sections: string[] }): ObservableResult<void> {
    return this.client
      .put<void>(Location.joinWithSlash(environment.apiRootUrl || '', `${basePath}/${playlistUid}/sections/reorder`), request)
      .pipe(
        switchMap(() => ObservableResult.ofSuccess()),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorReorderSections'))),
      );
  }

  publishProgram(programUid: string): ObservableResult<void> {
    return this.client.post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${programUid}/publication`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorPublishProgram'))),
    );
  }

  unpublishProgram(programUid: string): ObservableResult<void> {
    return this.client.delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${programUid}/publication`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUnpublishProgram'))),
    );
  }

  archiveProgram(programUid: string): ObservableResult<void> {
    return this.client.post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${programUid}/archives`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorArchiveProgram'))),
    );
  }

  discardProgram(programUid: string): ObservableResult<void> {
    return this.client.delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${programUid}/discard`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorDiscardProgram'))),
    );
  }

  createCollection(request: ProgramCreationRequest): ObservableResult<CreateProgramResponse> {
    return this.createPlaylist(request, 'COLLECTION');
  }

  getAdminCollections(orgClientId?: string, requestParams?: ProgramsRequest): ObservableResult<Page<ProgramItem>> {
    return this.getAdminList('analytics/collections/admin', orgClientId, requestParams);
  }

  getCollectionByUri(uri: string, inParent?: string, languageCode?: string): ObservableResult<Program> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `collections/${uri}`);
    const params = inParent ? { in: inParent } : null;

    return this.client.get<Program>(url, params, languageCode ? { 'Accept-Language': languageCode } : null).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetProgramDetail'))),
    );
  }

  getCollectionMembers(collectionId: string, requestParams?: CollectionMembersRequest): ObservableResult<Page<Member>> {
    const params: Record<string, string> = {};
    if (requestParams?.page != null) {
      params['page'] = requestParams.page.toString();
    }
    if (requestParams?.size != null) {
      params['size'] = requestParams.size.toString();
    }
    if (requestParams?.term) {
      params['term'] = requestParams.term;
    }
    return this.client
      .get<Page<Member>>(Location.joinWithSlash(environment.apiRootUrl || '', `collections/${collectionId}/members`), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetMembers'))),
      );
  }

  addCollectionSection(
    collectionUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse> {
    return this.addSection('collections', collectionUid, request, languageCode);
  }

  updateCollectionSection(
    collectionUid: string,
    sectionUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse> {
    return this.updateSection('collections', collectionUid, sectionUid, request, languageCode);
  }

  bulkUpdateCollectionSections(collectionUid: string, sections: ProgramSectionBulkUpdateItem[]): ObservableResult<void> {
    return this.bulkUpdateSections('collections', collectionUid, sections);
  }

  deleteCollectionSection(collectionUid: string, sectionUid: string): ObservableResult<void> {
    return this.deleteSection('collections', collectionUid, sectionUid);
  }

  reorderCollectionSections(collectionUid: string, request: { sections: string[] }): ObservableResult<void> {
    return this.reorderSections('collections', collectionUid, request);
  }
}
